import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, GraduationCap, Mic, Code, Users, Camera, Languages, Hand, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Volunteer Opportunities & Registration — public page advertising volunteer
 * roles and accepting expressions of interest. Submissions are emailed to
 * info@ambedkaritebuddhistglobalnetworkuk.com and stored in the admin dashboard for
 * follow-up.
 */

const ROLES = [
  { id: "teaching", icon: GraduationCap, title: "Teaching support", desc: "Help facilitate weekly Dhamma study circles online or at UK community venues. Suitable for members confident in spoken English plus one community language." },
  { id: "events", icon: Hand, title: "Event organising", desc: "Coordinate logistics for in-person events (Stockport, Birmingham, London) and online festivals. Includes risk assessment, refreshments, AV setup." },
  { id: "translation", icon: Languages, title: "Content translation", desc: "Translate newsletter articles and Dhamma teachings into Marathi, Hindi, Punjabi, Gujarati, Bengali, Tamil, Telugu, Kannada, Malayalam or Urdu." },
  { id: "tech", icon: Code, title: "Technology & platform", desc: "Help maintain the cloud platform, video meeting infrastructure, content moderation tools and accessibility improvements. JavaScript / Python welcome but not required." },
  { id: "media", icon: Camera, title: "Media & content", desc: "Photography, videography, social media graphics, podcast editing, livestream production for festivals and Dhamma talks." },
  { id: "youth", icon: Users, title: "Youth wing", desc: "Help build and run the 13–25 youth chapter (book club, mentoring, Buddhist learning roadmap). DBS check provided." },
  { id: "pastoral", icon: Heart, title: "Pastoral & wellbeing", desc: "Welcoming new members, checking in on isolated elders, organising peer-to-peer support for community members." },
  { id: "voice", icon: Mic, title: "Voice & advocacy", desc: "Represent ABGN-UK at interfaith gatherings, contribute to public consultations on equality / caste discrimination, write opinion pieces for the newsletter." },
];

const COMMITMENT_OPTIONS = [
  "1–2 hours per month",
  "1–2 hours per week",
  "Half a day per week",
  "Project-based / one-off",
  "I'm flexible",
];

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }) };

export default function Volunteer() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    age_range: "",
    role_ids: [],
    languages: "",
    skills: "",
    commitment: "",
    motivation: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleRole = (id) => {
    setForm((f) => ({
      ...f,
      role_ids: f.role_ids.includes(id) ? f.role_ids.filter((x) => x !== id) : [...f.role_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm the data-protection acknowledgement before submitting.");
      return;
    }
    if (!form.role_ids.length) {
      toast.error("Please pick at least one role you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteer/register", form);
      setSubmitted(true);
      toast.success("Thank you. The volunteer team will be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please email info@ambedkaritebuddhistglobalnetworkuk.com");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-pearl py-24">
        <div className="section-container max-w-2xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-saffron/15 text-saffron mb-6">
            <Hand size={28} />
          </div>
          <h1 className="font-display text-4xl text-navy">Thank you for stepping forward.</h1>
          <p className="mt-5 text-slate-700 leading-relaxed max-w-lg mx-auto">
            Your expression of interest has reached the ABGN-UK volunteer team.
            We'll write to <strong>{form.email}</strong> within a week to talk about
            how you'd like to contribute.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Jai Bhim · Namo Buddhāya
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-8 px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm hover:bg-navy-light"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pearl">
      <section className="bg-gradient-to-b from-navy via-navy to-navy-light text-pearl">
        <div className="section-container py-16 sm:py-20">
          <p className="eyebrow text-saffron">Volunteer with us</p>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Lend your time, share your gifts, build the Sangha.
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="mt-6 text-lg text-pearl/80 max-w-3xl leading-relaxed">
            ABGN-UK is built and sustained by volunteers. Whether you have an hour a
            month or a day a week, there is a way to contribute. All roles are open
            regardless of caste, gender, age (16+), faith or background.
          </motion.p>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Open roles</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-navy">Pick the ones that suit you.</h2>
            <p className="mt-4 text-slate-700">Tick any number below. The volunteer team will follow up to find the best fit.</p>
          </div>

          <form onSubmit={submit} className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="volunteer-form">
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const active = form.role_ids.includes(r.id);
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => toggleRole(r.id)}
                    data-testid={`vol-role-${r.id}`}
                    className={`text-left bg-white border rounded-sm p-5 transition-all ${
                      active ? "border-saffron ring-2 ring-saffron/30 shadow-md" : "border-navy/10 hover:border-saffron/40"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${active ? "bg-saffron/20 text-saffron" : "bg-pearl text-navy/60"}`}>
                        <Icon size={18} strokeWidth={1.6} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-display text-lg text-navy leading-tight">{r.title}</h3>
                        <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{r.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <aside className="lg:col-span-1 bg-white border border-navy/10 rounded-sm p-6 space-y-4 h-fit" data-testid="volunteer-aside">
              <h3 className="font-display text-xl text-navy">Your details</h3>
              <Field label="Full name" required>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="form-input" required maxLength={120} data-testid="vol-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="form-input" required maxLength={160} data-testid="vol-email" />
              </Field>
              <Field label="Phone (optional)">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="form-input" maxLength={40} data-testid="vol-phone" />
              </Field>
              <Field label="Town / city">
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="form-input" maxLength={80} data-testid="vol-location" />
              </Field>
              <Field label="Age range">
                <select value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })}
                  className="form-input" data-testid="vol-age">
                  <option value="">Prefer not to say</option>
                  <option>16–18</option>
                  <option>19–25</option>
                  <option>26–35</option>
                  <option>36–50</option>
                  <option>51–65</option>
                  <option>66+</option>
                </select>
              </Field>
              <Field label="Languages you speak">
                <input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })}
                  placeholder="e.g. English, Marathi, Hindi"
                  className="form-input" maxLength={160} data-testid="vol-languages" />
              </Field>
              <Field label="Time commitment">
                <select value={form.commitment} onChange={(e) => setForm({ ...form, commitment: e.target.value })}
                  className="form-input" data-testid="vol-commitment">
                  <option value="">Select…</option>
                  {COMMITMENT_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Skills / experience (optional)">
                <textarea value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
                  className="form-input min-h-[60px]" maxLength={500} data-testid="vol-skills" />
              </Field>
              <Field label="Why do you want to volunteer with ABGN-UK?">
                <textarea value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                  className="form-input min-h-[80px]" maxLength={1000} data-testid="vol-motivation" />
              </Field>
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-0.5" data-testid="vol-consent" />
                <span>
                  I agree my details may be stored privately by ABGN-UK and used to contact me about
                  volunteering. I can ask for my data to be deleted any time.
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="vol-submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 bg-navy text-pearl rounded-sm text-sm font-medium hover:bg-navy-light disabled:opacity-50"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Submit interest"}
              </button>
            </aside>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-navy/60 mb-1">
        {label}{required && <span className="text-saffron"> *</span>}
      </span>
      {children}
    </label>
  );
}
